import React, { FunctionComponent, useState } from 'react';
import { Button, Text, Tooltip, TooltipContent, TooltipTrigger } from '@companion-professional/components';
import { Paperclip, RotateCcw } from 'lucide-react';

interface AddCheckupToSurveyWizardProps {
  onAllAnswersYes: () => void;
}

const questions = [
  'Is this the same pet as the other checkup(s) attached to the findings before?',
  'Does this checkup represent the findings below (you may update the findings in light of this new evidence if appropriate)?'
];

// AddCheckupToSurveyWizard is a wizard that asks the user a series of questions to determine if a checkup should be
// added to the survey answers.  If any of the answers are 'No', the checkup cannot be added and a message is
// displayed. If all answers are 'Yes', the onAllAnswersYes function is called after the last question.
export const AddCheckupToSurveyWizard: FunctionComponent<AddCheckupToSurveyWizardProps> = ({ onAllAnswersYes }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredNo, setAnsweredNo] = useState(false);
  const [toBeAttached, setToBeAttached] = useState(false);

  if (answeredNo) {
    return (
      <div className="flex min-h-36 flex-col items-center justify-center gap-4 rounded-lg border bg-widget px-4 py-2 text-on-widget">
        <Text tone="destructive">Sorry, this checkup cannot be added to the survey answers.</Text>
        <div>
          <Button
            size="small"
            onClick={() => {
              setToBeAttached(false);
              setCurrentQuestionIndex(0);
              setAnsweredNo(false);
            }}
            className="flex items-center gap-2"
          >
            <RotateCcw size={18} />
            Reset
          </Button>
        </div>
      </div>
    );
  }

  if (!toBeAttached) {
    return (
      <div className="flex min-h-36 w-full flex-col items-center justify-center">
        <Tooltip>
          <TooltipTrigger>
            <Button
              size="small"
              onClick={() => {
                setToBeAttached(true);
              }}
              className="flex items-center gap-2"
            >
              <Paperclip size={18} />
              Attach Checkup
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Attaches this checkup (along with any videos contained in the checkup) to the survey below.</p>
            <p>You will be prompted a couple questions to verify that this is for the same pet and findings.</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex min-h-36 flex-col justify-between gap-4 rounded-lg border bg-widget px-4 py-2 text-on-widget">
      <Text>{questions[currentQuestionIndex]}</Text>
      <div className="flex flex-row justify-center gap-2">
        <Button
          size="small"
          onClick={() => {
            if (currentQuestionIndex === questions.length - 1) {
              onAllAnswersYes();
            } else {
              setCurrentQuestionIndex(currentQuestionIndex + 1);
            }
          }}
        >
          Yes
        </Button>
        <Button size="small" onClick={() => setAnsweredNo(true)} variant="destructive">
          No
        </Button>
      </div>
    </div>
  );
};
